'use client';

import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, KeyRound, Gift, UserPlus, Clock, Globe } from 'lucide-react';
import { useRouter, useSearchParams } from 'next/navigation';
import { prefetchRegion, prefetchType } from '@/lib/priceCache';
import { usePriceConvert } from '@/lib/PriceContext';
import { useT } from '@/hooks/useT';

export type OfferType  = 'key' | 'gift' | 'account' | 'rental';
export type RegionCode = 'any' | 'AR' | 'AU' | 'BR' | 'CA' | 'CL' | 'CO' | 'GB' | 'HK' | 'IN' | 'JP' | 'KR' | 'MX' | 'NO' | 'NZ' | 'SA' | 'SE' | 'SG' | 'TR' | 'TW' | 'UA' | 'US' | 'ZA';

export const OFFER_ICONS: Record<OfferType, React.ReactNode> = {
  key:     <KeyRound size={12} aria-hidden />,
  gift:    <Gift     size={12} aria-hidden />,
  account: <UserPlus size={12} aria-hidden />,
  rental:  <Clock    size={12} aria-hidden />,
};

export const ALL_REGIONS: Array<{ code: RegionCode; label: string; flag: string }> = [
  { code: 'any', label: 'Any',                flag: '' },
  { code: 'AR',  label: 'Argentina',          flag: '🇦🇷' },
  { code: 'AU',  label: 'Australia',          flag: '🇦🇺' },
  { code: 'BR',  label: 'Brazil',             flag: '🇧🇷' },
  { code: 'CA',  label: 'Canada',             flag: '🇨🇦' },
  { code: 'CL',  label: 'Chile',              flag: '🇨🇱' },
  { code: 'CO',  label: 'Colombia',           flag: '🇨🇴' },
  { code: 'GB',  label: 'UK',                 flag: '🇬🇧' },
  { code: 'HK',  label: 'Hong Kong',          flag: '🇭🇰' },
  { code: 'IN',  label: 'India',              flag: '🇮🇳' },
  { code: 'JP',  label: 'Japan',              flag: '🇯🇵' },
  { code: 'KR',  label: 'South Korea',        flag: '🇰🇷' },
  { code: 'MX',  label: 'Mexico',             flag: '🇲🇽' },
  { code: 'NO',  label: 'Norway',             flag: '🇳🇴' },
  { code: 'NZ',  label: 'New Zealand',        flag: '🇳🇿' },
  { code: 'SA',  label: 'Saudi Arabia',       flag: '🇸🇦' },
  { code: 'SE',  label: 'Sweden',             flag: '🇸🇪' },
  { code: 'SG',  label: 'Singapore',          flag: '🇸🇬' },
  { code: 'TR',  label: 'Turkey',             flag: '🇹🇷' },
  { code: 'TW',  label: 'Taiwan',             flag: '🇹🇼' },
  { code: 'UA',  label: 'Ukraine',            flag: '🇺🇦' },
  { code: 'US',  label: 'USA',                flag: '🇺🇸' },
  { code: 'ZA',  label: 'South Africa',       flag: '🇿🇦' },
];
export const REGION_MAP = Object.fromEntries(ALL_REGIONS.map(r => [r.code, r])) as Record<RegionCode, typeof ALL_REGIONS[0]>;
export const OFFER_MULTS: Record<OfferType, number> = {
  key: 1.15, gift: 1.20, account: 1.20, rental: 1.0,
};

// Публічні параметри deep-link: ?offer=<key|gift|account|rental>&region=<KR|US|...>.
// Читаємо нові імена, з fallback на старі (type/r) — щоб раніше поширені лінки не ламались.
export function readOfferParams(p: URLSearchParams): { offerType: OfferType; region: RegionCode } {
  const ot = (p.get('offer') ?? p.get('type')) as OfferType | null;
  const r  = (p.get('region') ?? p.get('r'))   as RegionCode | null;
  const vt: OfferType[] = ['key','gift','account','rental'];
  const vr = ALL_REGIONS.map(x => x.code);
  return {
    offerType: (ot && vt.includes(ot)) ? ot : 'key',
    region:    (r && vr.includes(r as RegionCode)) ? r : 'any',
  };
}

// Централізований суфікс для лінків на PDP з картки: несе поточний тип видачі + регіон,
// щоб PDP відкрився на тому самому offer/region (і тій самій «від»-ціні). Дефолти опускаємо.
export function offerLinkSuffix(offerType: OfferType, region: RegionCode): string {
  const p = new URLSearchParams();
  if (offerType !== 'key') p.set('offer', offerType);
  if (region !== 'any')    p.set('region', region);
  const q = p.toString();
  return q ? `?${q}` : '';
}

/* -- module-level market config (fetched once per session) -- */
type MarketConfig = Record<string, { blocked_offer_types: string[] }>;
let marketConfigCache: MarketConfig | null = null;
async function getMarketConfig(): Promise<MarketConfig> {
  if (marketConfigCache) return marketConfigCache;
  try {
    const r = await fetch('/api/market-config');
    marketConfigCache = await r.json();
  } catch { marketConfigCache = {}; }
  return marketConfigCache!;
}

function isTypeBlocked(config: MarketConfig | null, region: RegionCode, type: OfferType): boolean {
  if (!config || region === 'any') return false;
  const blocked = config[region]?.blocked_offer_types ?? [];
  return blocked.includes(type) || blocked.includes('all');
}

function firstAvailableType(config: MarketConfig | null, region: RegionCode): OfferType | null {
  for (const ot of ['gift', 'rental', 'account', 'key'] as OfferType[]) {
    if (!isTypeBlocked(config, region, ot)) return ot;
  }
  return null;
}

/* -- portal dropdown hook -- */
function useDropdown() {
  const [open, setOpen] = useState(false);
  const [pos,  setPos]  = useState({ top: 0, left: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const dropId = useRef(`dd-${Math.random().toString(36).slice(2)}`).current;

  useEffect(() => {
    const h = (e: MouseEvent) => {
      const tgt = e.target as Node;
      const d = document.getElementById(dropId);
      if (!btnRef.current?.contains(tgt) && !d?.contains(tgt)) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [dropId]);

  function toggle() {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 6, left: Math.min(rect.left, window.innerWidth - 220) });
    }
    setOpen(o => !o);
  }

  return { open, setOpen, pos, btnRef, dropId, toggle };
}

const optCss = (active: boolean, blocked = false) => ({
  display: 'flex', alignItems: 'center', gap: 7,
  padding: '7px 10px', borderRadius: 7, fontSize: 13, cursor: blocked ? 'not-allowed' : 'pointer',
  background: active ? 'color-mix(in srgb, var(--gs-green) 14%, transparent)' : 'transparent', border: 'none',
  color: blocked ? 'var(--v4-fg-4)' : active ? 'var(--gs-green)' : 'var(--v4-fg-2)',
  fontWeight: active ? 600 : 400, width: '100%', textAlign: 'left' as const,
  opacity: blocked ? 0.5 : 1,
});
function SLabel({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 10, color: 'var(--v4-fg-3)', fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', padding: '0 2px', marginBottom: 5 }}>{children}</div>;
}
function Panel({ id, top, left, children }: { id: string; top: number; left: number; children: React.ReactNode }) {
  return createPortal(
    <div id={id} className="gs-dropdown" style={{ position: 'fixed', top, left, zIndex: 9999, padding: '10px 8px', minWidth: 168 }}>
      {children}
    </div>,
    document.body
  );
}

/* ════════════════════════════════════════════
   DeliveryTypeFilter
   ════════════════════════════════════════════ */
interface TypeProps {
  offerType: OfferType;
  region: RegionCode;
  config: MarketConfig | null;
  onChange: (ot: OfferType) => void;
}

function DeliveryTypeFilter({ offerType, region, config, onChange }: TypeProps) {
  const { open, setOpen, pos, btnRef, dropId, toggle } = useDropdown();
  const t = useT();

  const OFFER_LABELS_T: Record<OfferType, string> = {
    key: t('offer_key'), gift: t('offer_gift'), account: t('offer_account'), rental: t('offer_rental'),
  };

  function handleOpen() {
    if (region !== 'any') prefetchRegion(region);
    toggle();
  }

  function select(ot: OfferType) {
    if (isTypeBlocked(config, region, ot)) return;
    onChange(ot);
    setOpen(false);
  }

  // Тип видачі обраний завжди (за замовч. «Ключ») → чіп завжди активний/з обводкою.
  const types: OfferType[] = ['key','gift','account','rental'];

  return (
    <>
      <button ref={btnRef} onClick={handleOpen} aria-haspopup="listbox" aria-expanded={open} aria-label={t('offer_type_title')} className="va-chip on" style={{ gap: 5 }}>
        {OFFER_ICONS[offerType]}
        <span>{OFFER_LABELS_T[offerType]}</span>
        <ChevronDown size={11} style={{ opacity: .5, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s', marginLeft: 1 }} />
      </button>
      {open && typeof document !== 'undefined' && (
        <Panel id={dropId} top={pos.top} left={pos.left}>
          <SLabel>{t('offer_type_title')}</SLabel>
          {types.map(ot => {
            const blocked = isTypeBlocked(config, region, ot);
            return (
              <button key={ot} onClick={() => select(ot)} title={blocked ? `${t('offer_unavail_for')} ${region}` : undefined} style={optCss(offerType === ot, blocked)}>
                {OFFER_ICONS[ot]}
                <span>{OFFER_LABELS_T[ot]}</span>
                {blocked && <span style={{ marginLeft: 'auto', fontSize: 10, color: 'rgba(255,80,80,.5)' }}>{t('offer_na')}</span>}
              </button>
            );
          })}
        </Panel>
      )}
    </>
  );
}

/* ════════════════════════════════════════════
   RegionFilter
   ════════════════════════════════════════════ */
interface RegionProps {
  offerType: OfferType;
  region: RegionCode;
  onChange: (r: RegionCode) => void;
}

function RegionFilter({ offerType, region, onChange }: RegionProps) {
  const { open, setOpen, pos, btnRef, dropId, toggle } = useDropdown();
  const { lang } = usePriceConvert();
  const t = useT();

  const REGION_LABELS_T: Partial<Record<string, string>> = {
    AR: lang === 'uk' ? 'Аргентина' : lang === 'ru' ? 'Аргентина' : 'Argentina',
    AU: lang === 'uk' ? 'Австралія' : lang === 'ru' ? 'Австралия' : 'Australia',
    BR: lang === 'uk' ? 'Бразилія' : lang === 'ru' ? 'Бразилия' : 'Brazil',
    CA: lang === 'uk' ? 'Канада' : lang === 'ru' ? 'Канада' : 'Canada',
    CL: lang === 'uk' ? 'Чилі' : lang === 'ru' ? 'Чили' : 'Chile',
    CO: lang === 'uk' ? 'Колумбія' : lang === 'ru' ? 'Колумбия' : 'Colombia',
    GB: lang === 'uk' ? 'Великобританія' : lang === 'ru' ? 'Великобритания' : 'UK',
    HK: lang === 'uk' ? 'Гонконг' : lang === 'ru' ? 'Гонконг' : 'Hong Kong',
    IN: lang === 'uk' ? 'Індія' : lang === 'ru' ? 'Индия' : 'India',
    JP: lang === 'uk' ? 'Японія' : lang === 'ru' ? 'Япония' : 'Japan',
    KR: lang === 'uk' ? 'Корея' : lang === 'ru' ? 'Корея' : 'South Korea',
    MX: lang === 'uk' ? 'Мексика' : lang === 'ru' ? 'Мексика' : 'Mexico',
    NO: lang === 'uk' ? 'Норвегія' : lang === 'ru' ? 'Норвегия' : 'Norway',
    NZ: lang === 'uk' ? 'Нова Зеландія' : lang === 'ru' ? 'Новая Зеландия' : 'New Zealand',
    SA: lang === 'uk' ? 'Саудівська Аравія' : lang === 'ru' ? 'Саудовская Аравия' : 'Saudi Arabia',
    SE: lang === 'uk' ? 'Швеція' : lang === 'ru' ? 'Швеция' : 'Sweden',
    SG: lang === 'uk' ? 'Сінгапур' : lang === 'ru' ? 'Сингапур' : 'Singapore',
    TR: lang === 'uk' ? 'Туреччина' : lang === 'ru' ? 'Турция' : 'Turkey',
    TW: lang === 'uk' ? 'Тайвань' : lang === 'ru' ? 'Тайвань' : 'Taiwan',
    UA: lang === 'uk' ? 'Україна' : lang === 'ru' ? 'Украина' : 'Ukraine',
    US: lang === 'uk' ? 'США' : lang === 'ru' ? 'США' : 'USA',
    ZA: lang === 'uk' ? 'Пд. Африка' : lang === 'ru' ? 'Юж. Африка' : 'South Africa',
  };

  function getRegionLabel(code: RegionCode): string {
    if (code === 'any') return t('offer_region_any');
    return REGION_LABELS_T[code] ?? code;
  }

  function handleOpen() {
    prefetchType(offerType, ALL_REGIONS.filter(r => r.code !== 'any').map(r => r.code));
    toggle();
  }

  function select(r: RegionCode) {
    onChange(r);
    setOpen(false);
  }

  const active = region !== 'any';

  return (
    <>
      <button ref={btnRef} onClick={handleOpen} aria-haspopup="listbox" aria-expanded={open} aria-label={t('filter_region')} className={`va-chip${active ? ' on' : ''}`} style={{ gap: 5 }}>
        {active
          ? <span style={{ fontSize: 15, lineHeight: 1 }}>{REGION_MAP[region]?.flag ?? ''}</span>
          : <Globe size={12} aria-hidden />}
        <span>{active ? getRegionLabel(region) : t('filter_region')}</span>
        <ChevronDown size={11} style={{ opacity: .5, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s', marginLeft: 1 }} />
      </button>
      {open && typeof document !== 'undefined' && (
        <Panel id={dropId} top={pos.top} left={pos.left}>
          <SLabel>{t('filter_region')}</SLabel>
          <div style={{ maxHeight: 380, overflowY: 'auto' }}>
            {ALL_REGIONS.map(r => (
              <button key={r.code} onClick={() => select(r.code)} style={optCss(region === r.code)}>
                {r.flag
                  ? <span style={{ fontSize: 15, lineHeight: 1, width: 20, textAlign: 'center' }}>{r.flag}</span>
                  : <Globe size={13} style={{ opacity: .4, flexShrink: 0 }} />}
                <span>{getRegionLabel(r.code)}</span>
                {r.code !== 'any' && <span style={{ marginLeft: 'auto', fontSize: 11, opacity: .3, fontFamily: 'monospace' }}>{r.code}</span>}
              </button>
            ))}
          </div>
        </Panel>
      )}
    </>
  );
}

/* ════════════════════════════════════════════
   OfferFilter — wrapper (single source of truth for URL)
   ════════════════════════════════════════════ */
export function OfferFilter({
  basePath,
  onOfferChange,
  offerType: offerTypeProp,
  region: regionProp,
}: {
  basePath: string;
  onOfferChange?: (o: OfferType, r: RegionCode) => void;
  // Контрольований режим (опційно) — щоб уникнути розсинхрону з батьківським станом:
  // без цих props компонент сам читає URL через useSearchParams(), і після router.replace()
  // під певними умовами (швидкі повторні зміни інших фільтрів у батьківському компоненті)
  // це може показувати застаріле значення в кнопці, хоча сам вибір застосувався коректно.
  offerType?: OfferType;
  region?: RegionCode;
}) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const fromUrl = readOfferParams(searchParams);
  const offerType = offerTypeProp ?? fromUrl.offerType;
  const region    = regionProp    ?? fromUrl.region;
  const [config, setConfig] = useState<MarketConfig | null>(null);

  useEffect(() => { getMarketConfig().then(setConfig); }, []);

  const didAutoSwitch = useRef(false);
  useEffect(() => {
    if (!config || didAutoSwitch.current) return;
    if (region !== 'any' && isTypeBlocked(config, region, offerType)) {
      const fallback = firstAvailableType(config, region);
      if (fallback) {
        didAutoSwitch.current = true;
        applyChange(fallback, region);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  function applyChange(newType: OfferType, newRegion: RegionCode) {
    const p = new URLSearchParams(window.location.search);
    p.delete('r'); // legacy region-param (offer-region, не плутати з product-type `type`)
    if (newType === 'key') p.delete('offer'); else p.set('offer', newType);
    if (newRegion === 'any') p.delete('region'); else p.set('region', newRegion);
    router.replace(`${basePath}?${p.toString()}`, { scroll: false });
    onOfferChange?.(newType, newRegion);
  }

  function handleTypeChange(newType: OfferType) {
    applyChange(newType, region);
  }

  function handleRegionChange(newRegion: RegionCode) {
    let effectiveType = offerType;
    if (newRegion !== 'any' && isTypeBlocked(config, newRegion, offerType)) {
      const fallback = firstAvailableType(config, newRegion);
      if (fallback) effectiveType = fallback;
    }
    applyChange(effectiveType, newRegion);
  }

  return (
    <>
      <DeliveryTypeFilter
        offerType={offerType}
        region={region}
        config={config}
        onChange={handleTypeChange}
      />
      <RegionFilter
        offerType={offerType}
        region={region}
        onChange={handleRegionChange}
      />
    </>
  );
}
